import { Flex, Text } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { ToolOutlined } from "@/icons"
import { DetailField as Field } from "./DetailField"

import styles from "./ViolationDetails.module.scss"

import type { AiPolicyDecisionRow } from "@/pages/GovernancePage/applications/ai-policies/useLiveAiPolicies.types"

// A violation's expanded row: who broke the rule and the evidence of it. What
// the decisions do not record says it is not available
export const ViolationDetails = ({
  violation,
  matchedRule
}: {
  violation: AiPolicyDecisionRow
  // The policy has a single rule group, so the violation matched it
  matchedRule: boolean
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "governance.ai.live.violationLog"
  })
  const notAvailable = t("notAvailable")
  // An MCP server policy's violation is by the server's tool, in the server's
  // workload: no agent and no model of one
  const { mcpServerName } = violation
  // The enforcement point names where the call was caught and by what; one
  // the log does not know is shown as the API names it
  const enforcer = violation.enforcedBy
  const foundInKey = mcpServerName ? "foundInMcpServer" : "foundIn"
  const foundIn =
    t(`${foundInKey}.${enforcer}`, { defaultValue: "" }) || notAvailable
  const enforcedBy = t(`enforcedBy.${enforcer}`, { defaultValue: enforcer })
  const blockedOrMonitored = t(
    `actions.${violation.blocked ? "blocked" : "monitored"}`
  )
  // A decision standing for several identical calls says how many
  const action =
    (violation.count ?? 1) > 1
      ? t("actionCount", { action: blockedOrMonitored, count: violation.count })
      : blockedOrMonitored

  return (
    <Flex vertical className={styles.details}>
      <Flex vertical gap={12} className={styles.detailsSection}>
        <Text strong>{t("whoViolated")}</Text>
        <div className={styles.detailsGrid}>
          {mcpServerName && (
            <Field label={t("fields.mcpServer")}>{mcpServerName}</Field>
          )}
          <Field label={t("fields.source")}>{violation.platformLabel}</Field>
          <Field label={t("fields.application")}>
            {violation.workloadLabel ?? notAvailable}
          </Field>
          <Field label={t("fields.image")}>
            {violation.image || notAvailable}
          </Field>
          <Field label={t("fields.type")}>{notAvailable}</Field>
          <Field label={t("fields.repository")}>{notAvailable}</Field>
          <Field label={t("fields.environment")}>
            {violation.environment || notAvailable}
          </Field>
          {!mcpServerName && (
            <Field label={t("fields.model")}>{notAvailable}</Field>
          )}
        </div>
      </Flex>
      <Flex vertical gap={12} className={styles.detailsSection}>
        <Text strong>{t("evidence")}</Text>
        <div className={styles.detailsGrid}>
          <Field label={t("fields.whatBroke")}>
            {matchedRule
              ? t("whatBroke", { tool: violation.tool })
              : notAvailable}
          </Field>
          <Field label={t("fields.reason")}>
            {violation.reason || notAvailable}
          </Field>
          <Field label={t("fields.tool")}>
            <Flex align="center" gap={6}>
              <ToolOutlined />
              {violation.tool}
            </Flex>
          </Field>
          <Field label={t("fields.foundIn")}>{foundIn}</Field>
          <Field label={t("fields.action")}>{action}</Field>
          <Field label={t("fields.alsoOnViolation")}>{notAvailable}</Field>
          <Field label={t("fields.enforcedBy")}>{enforcedBy}</Field>
        </div>
      </Flex>
    </Flex>
  )
}
